/**
 * Track position calculation utilities
 * Pure functions for converting between slide indices and track positions
 */

/**
 * Convert slide index to track position
 * @param {number} slideIndex - The index of the slide
 * @param {Object} widths - Width measurements from store
 * @param {Object} options - Carousel options from store
 * @param {Object} state - Carousel state from store
 * @returns {number} The position on the track (negative for transform)
 */
export function getTrackPosForIndex(slideIndex, widths, options, state) {
	// Raw position of slide's left edge on the track
	const slidePos = slideIndex * (widths.slide + widths.gap);

	let pos;

	if (options.centerSelectedSlide) {
		// Center the slide in the viewport
		pos = slidePos - widths.viewport / 2 + widths.slide / 2;
	} else {
		// Left-align: account for left padding
		pos = slidePos - widths.paddingLeft;
	}

	// Apply boundary clamping (only when looping is not possible)
	if (!state.canLoop) {
		const minPos = -widths.paddingLeft;
		const maxPos = widths.track - widths.viewport - widths.gap + widths.paddingRight;
		pos = Math.max(minPos, Math.min(pos, maxPos));
	}

	// Transform values are negative (moving track left = positive pos)
	return pos !== 0 ? -pos : 0;
}

/**
 * Convert track position to slide index (inverse of getTrackPosForIndex)
 * @param {number} trackPosition - Current track position (negative for transform)
 * @param {Object} widths - Width measurements from store
 * @param {Object} options - Carousel options from store
 * @param {number} [velocity=0] - Current velocity for tie-breaking
 * @param {number} [slideCount=Infinity] - Total number of slides for clamping
 * @returns {number} The nearest slide index
 */
export function getIndexForTrackPos(trackPosition, widths, options, velocity = 0, slideCount = Infinity) {
	const slideAndGap = widths.slide + widths.gap;

	// Guard against division by zero
	if (slideAndGap <= 0) return 0;

	// Convert from transform space (negative) to position space (positive)
	let absPos = Math.abs(trackPosition);

	// Reverse the centering adjustment if enabled
	if (options.centerSelectedSlide) {
		absPos = absPos + widths.viewport / 2 - widths.slide / 2;
	} else {
		// Reverse the left padding adjustment
		absPos = absPos + widths.paddingLeft;
	}

	const rawIndex = absPos / slideAndGap;

	let index;

	// Use velocity direction for tie-breaking when exactly between slides
	const fractionalPart = rawIndex - Math.floor(rawIndex);
	if (velocity !== 0 && Math.abs(fractionalPart - 0.5) < 0.01) {
		// Exactly between slides - use velocity to decide direction
		index = velocity < 0 ? Math.ceil(rawIndex) : Math.floor(rawIndex);
	} else {
		index = Math.round(rawIndex);
	}

	// Clamp to valid range
	return Math.max(0, Math.min(index, slideCount - 1));
}
